import { api } from '@/lib/api';
import { AdminLoginRequest, AuthContextType, LoginRedirectRequest, LoginRequest, ResetPasswordRequest, User, VerifyEmailRequest, VerifySmsRequest } from '@/types';
import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router";
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: any) => {
    const [currentUser, setCurrentUser] = useState<User | null>(localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') as string) : null);
    const [loading, setLoading] = useState<boolean>(true);
    const navigate = useNavigate();

    api.interceptors.response.use((response) => {
        return response
    }, (error) => {
        if (error.response?.status === 401) {
            window.localStorage.removeItem('user');
            setCurrentUser(null);
            navigate('/');
        }
        return Promise.reject(error);
    });

    const adminLogin = async (loginRequest: AdminLoginRequest) => {
        try {
            setLoading(true);
            const response: any = await api.post('admin/auth/login', loginRequest);
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
            navigate('/admin');
        } catch (error) {
            // console.log(error);
            setCurrentUser(null);
        } finally {
            setLoading(false);
        }
    }

    const login = (loginRequest: LoginRequest) => {
        try {
            setLoading(true);
            api.post('auth/login?email=' + loginRequest.email);
        } catch (error) {
            // console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const impersonate = async (userId: number) => {
        try {
            setLoading(true);
            const response: any = await api.post('admin/impersonate', { userId });
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
            navigate('/dashboard');
        } catch (error) {
            // console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoading(true);
                const userJson = window.localStorage.getItem('user');
                const user = userJson ? JSON.parse(userJson) : null;
                if (user) {
                    const response: any = await api.post('auth/me')
                    setCurrentUser({...response});
                }            
                setLoading(false);
            } catch (error) {
                // console.log(error);
                setCurrentUser(null);
            } finally {
                setLoading(false);
            }
        }
        fetchUser();
    }, [])
    const loginRedirect = async (loginRedirectRequest: LoginRedirectRequest) => {
        try {
            setLoading(true);
            await logout(); // Ensure we clear any previous user state
            const response: any = await api.post('auth/code?code=' + loginRedirectRequest.code);
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
        } catch (error: any) {
            // console.log(error);
            setCurrentUser(null);
            navigate('/')
        }
        finally {
            setLoading(false);
        }
    }

    const verifyEmail = async (verifyEmailRequest: VerifyEmailRequest) => {
        try {
            setLoading(true);
            const response: any = await api.post('auth/verify-email?code=' + verifyEmailRequest.code);
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
        } catch (error) {
            // console.log(error);
            setCurrentUser(null);
            navigate('/')
        }
        finally {
            setLoading(false);
        }
    }

    const verifySMS = async (verifySmsRequest: VerifySmsRequest) => {
        try {
            setLoading(true);
            const response: any = await api.post('auth/verify-sms?code=' + verifySmsRequest.code);
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
        } catch (error) {
            // console.log(error);
            setCurrentUser(null);
            navigate('/')
        } finally {
            setLoading(false);
        }
    }

    const resetPassword = async (resetPasswordRequest: ResetPasswordRequest): Promise<boolean> => {
        try {
            setLoading(true);
            const response: any = await api.post('auth/reset-password', resetPasswordRequest);
            setCurrentUser(response);
            window.localStorage.setItem('user', JSON.stringify(response));
            return true;
        } catch (error) {
            // console.log(error);
            setCurrentUser(null);
            return false;
        }
        finally {
            setLoading(false);
        }
    }

    const logout = async () => {

        try {
            setLoading(true);
            window.localStorage.removeItem('user');
            setCurrentUser(null);
            const response: any =  await api.get('auth/logout');
            // Handle if the user was impersonated
            if(response.id) {
                setCurrentUser(response);
                window.localStorage.setItem('user', JSON.stringify(response));
                navigate('/admin');
            }
        } catch (error) {
            // console.log(error);
            setCurrentUser(null);
            navigate('/')
        } finally {
            setLoading(false);
        }
    }

    const setAuthenticatedUser = (user: User) => {
        if (user && user.id && user.email) {
            setCurrentUser(user);
            window.localStorage.setItem('user', JSON.stringify(user));
        }
    }

    const value = {
        currentUser,
        loading,
        login,
        adminLogin,
        impersonate,
        loginRedirect,
        verifyEmail,
        verifySMS,
        logout,
        resetPassword,
        setAuthenticatedUser
    }



    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}